import { useState } from 'react'
import { FeedbackVote, IChatAnalyticsKpis, IChatAnalyticsSummary, IChatFeedbackSummary } from '../models'
import {
  formatCount,
  formatDuration,
  formatPercent,
  formatTargetValue,
  P95_TARGET_MS,
  p95TargetStatus,
  targetClass,
} from '../viewModel'
import ChatbotAnalyticsCharts from './ChatbotAnalyticsCharts'
import styles from './ChatbotAnalytics.module.scss'

interface KpiDefinition {
  key: keyof IChatAnalyticsKpis
  label: string
  format: (value: number) => string
}

const kpiDefinitions: KpiDefinition[] = [
  { key: 'TotalTurns', label: 'Toplam Tur', format: formatCount },
  { key: 'ActiveDealers', label: 'Aktif Bayi', format: formatCount },
  { key: 'TechnicalSuccessRate', label: 'Teknik Başarı', format: formatPercent },
  { key: 'P50DurationMs', label: 'p50 Yanıt Süresi', format: formatDuration },
  { key: 'P95DurationMs', label: 'p95 Yanıt Süresi', format: formatDuration },
]

/** Geri bildirim KPI'ları `Feedback` bloğundan okunur (üsttekiler `Summary`'den). */
const feedbackKpiDefinitions: {
  key: keyof IChatFeedbackSummary
  label: string
  format: (value: number) => string
}[] = [
  { key: 'PositiveRate', label: 'Olumlu Geri Bildirim', format: formatPercent },
  { key: 'CoverageRate', label: 'Geri Bildirim Kapsamı', format: formatPercent },
  { key: 'TotalVotes', label: 'Toplam Oy', format: formatCount },
]

/** p50/p95 kartlarının yanında "ⓘ" açıklaması vardır (Faz-37 §1b). */
const durationKpiKeys = new Set<keyof IChatAnalyticsKpis>(['P50DurationMs', 'P95DurationMs'])

interface ChatbotAnalyticsContentProps {
  data: IChatAnalyticsSummary
  /** Memnuniyetteki olumlu/olumsuz sayısına tıklanınca Geri Bildirimler sekmesi o oya süzülür. */
  onShowVotes?: (vote: FeedbackVote) => void
}

/** Sayfanın gerçek değerleriyle p50 / p95 açıklaması (yalnız ön yüz). */
function DurationInfoPanel({ kpis, onClose }: { kpis: IChatAnalyticsKpis; onClose: () => void }) {
  const p50 = formatDuration(kpis.P50DurationMs)
  const p95 = formatDuration(kpis.P95DurationMs)
  const target = p95TargetStatus(kpis.P95DurationMs)

  return (
    <section
      className={styles.durationInfo}
      id='chatbot-duration-info'
      data-testid='duration-info'
      aria-labelledby='chatbot-duration-info-title'
    >
      <div className={styles.durationInfoHeader}>
        <h3 id='chatbot-duration-info-title'>p50 ve p95 ne anlatır?</h3>
        <button type='button' className={styles.infoClose} aria-label='Açıklamayı kapat' onClick={onClose}>
          ×
        </button>
      </div>
      <dl>
        <dt>p50 (medyan) · {p50}</dt>
        <dd>
          Soruların yarısı bu sürede ya da daha kısa sürede cevaplandı; bayinin tipik bekleme süresidir. Bu aralıkta
          her iki sorudan biri en geç {p50} içinde cevaplandı.
        </dd>
        <dt>p95 · {p95}</dt>
        <dd>
          Soruların %95&apos;i bu süreyi aşmadı; en yavaş %5 bunun üstünde. Yavaş kuyruğu gösterir: bu aralıkta her
          100 sorudan 95&apos;i en geç {p95} içinde cevaplandı.
        </dd>
      </dl>
      <p>
        Ortalama ({formatDuration(kpis.AverageDurationMs)}) birkaç çok yavaş cevapla şişebilir; tipik deneyim için
        p50&apos;ye, yavaşlık sorunları için p95&apos;e bakın.
      </p>
      <p className={target.passed ? styles.durationTargetPassed : styles.durationTargetFailed}>
        Hedef: p95 ≤ {formatDuration(P95_TARGET_MS)} · {target.text}
      </p>
    </section>
  )
}

/** Backend `Feedback` bloğunu göndermezse (eski sürüm) beyaz ekran yerine boş duruma düşülür. */
const emptyFeedback: IChatFeedbackSummary = {
  TotalVotes: 0,
  PositiveVotes: 0,
  NegativeVotes: 0,
  PositiveRate: 0,
  CoverageRate: 0,
  DailyTrend: [],
  ReasonBreakdown: [],
  IntentFeedback: [],
  OutcomeFeedback: [],
}

export default function ChatbotAnalyticsContent({ data, onShowVotes }: ChatbotAnalyticsContentProps) {
  const isEmpty = data.Summary.TotalTurns === 0
  const feedback = data.Feedback ?? emptyFeedback
  const resolvedNegativeVotes = feedback.ResolvedNegativeVotes ?? 0
  const [durationInfoOpen, setDurationInfoOpen] = useState(false)

  const resolvedNote =
    resolvedNegativeVotes > 0 ? (
      <p className={styles.resolvedNote} data-testid='feedback-resolved-note'>
        {formatCount(resolvedNegativeVotes)} kapatılmış olumsuz geri bildirim memnuniyet hesabına dahil değildir.
      </p>
    ) : null

  return (
    <div className={styles.page} id='chatbot-analytics-results'>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>Chatbot Analitiği</h1>
          <p>Pilot kullanımının teknik başarısını, hızını ve kullanım dağılımını aggregate verilerle izleyin.</p>
        </div>
        <span className={styles.privacyBadge}>Yalnızca aggregate veri</span>
      </header>

      {isEmpty ? (
        <section className={styles.statePanel} role='status'>
          <span className={styles.stateIcon} aria-hidden='true'>
            —
          </span>
          <h2>Henüz veri yok</h2>
          <p>Seçilen tarih aralığında chatbot verisi bulunamadı.</p>
        </section>
      ) : (
        <>
          <section className={styles.kpiGrid} aria-label='Temel performans göstergeleri'>
            {kpiDefinitions.map(definition => (
              <article className={styles.kpiCard} key={definition.key}>
                <span className={styles.kpiLabel}>
                  {definition.label}
                  {durationKpiKeys.has(definition.key) ? (
                    <button
                      type='button'
                      className={styles.infoButton}
                      data-testid={`kpi-info-${definition.key}`}
                      aria-label={`${definition.label} açıklaması`}
                      aria-expanded={durationInfoOpen}
                      aria-controls='chatbot-duration-info'
                      onClick={() => setDurationInfoOpen(current => !current)}
                    >
                      ⓘ
                    </button>
                  ) : null}
                </span>
                <strong>{definition.format(data.Summary[definition.key])}</strong>
              </article>
            ))}
          </section>

          {durationInfoOpen ? (
            <DurationInfoPanel kpis={data.Summary} onClose={() => setDurationInfoOpen(false)} />
          ) : null}

          <section className={styles.targetSection} aria-labelledby='chatbot-targets-title'>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>Pilot karar eşiği</span>
                <h2 id='chatbot-targets-title'>Hedef Durumu</h2>
              </div>
              <span>{formatCount(data.Targets.filter(target => target.Passed).length)} hedef karşılandı</span>
            </div>
            <div className={styles.targetGrid}>
              {data.Targets.map(target => {
                const status = targetClass(target.Passed)
                return (
                  <article className={`${styles.targetCard} ${styles[status]}`} key={target.Key}>
                    <div className={styles.targetStatus}>
                      <span className={styles.statusDot} aria-hidden='true' />
                      {target.Passed ? 'Hedefte' : 'Hedef altında'}
                    </div>
                    <h3>{target.Label}</h3>
                    <strong>{formatTargetValue(target, target.Actual)}</strong>
                    <span>Hedef: {formatTargetValue(target, target.Target)}</span>
                  </article>
                )
              })}
            </div>
          </section>

          <section className={styles.feedbackSection} aria-labelledby='chatbot-feedback-title'>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>Kullanıcı geri bildirimi</span>
                <h2 id='chatbot-feedback-title'>Memnuniyet</h2>
              </div>
              <span className={styles.feedbackHint}>
                Kapsam düşükken olumlu oran tek başına yanıltıcıdır; iki değer birlikte okunur.
              </span>
            </div>

            {feedback.TotalVotes === 0 ? (
              <>
                <p className={styles.feedbackEmpty} role='status'>
                  Bu aralıkta geri bildirim yok
                </p>
                {resolvedNote}
              </>
            ) : (
              <>
                <div className={styles.kpiGrid}>
                  {feedbackKpiDefinitions.map(definition => (
                    <article className={styles.kpiCard} key={definition.key}>
                      <span>{definition.label}</span>
                      <strong>{definition.format(feedback[definition.key] as number)}</strong>
                    </article>
                  ))}
                </div>

                {/* Sayıya tıklayınca Geri Bildirimler sekmesi o oya süzülmüş açılır (Faz-37 §1d). */}
                <div className={styles.voteLinks} role='group' aria-label='Oylara git'>
                  <button
                    type='button'
                    className={`${styles.voteLink} ${styles.voteLinkUp}`}
                    data-testid='feedback-show-up'
                    onClick={() => onShowVotes?.('up')}
                  >
                    <span>👍 Olumlu</span>
                    <strong>{formatCount(feedback.PositiveVotes)}</strong>
                  </button>
                  <button
                    type='button'
                    className={`${styles.voteLink} ${styles.voteLinkDown}`}
                    data-testid='feedback-show-down'
                    onClick={() => onShowVotes?.('down')}
                  >
                    <span>👎 Olumsuz</span>
                    <strong>{formatCount(feedback.NegativeVotes)}</strong>
                  </button>
                </div>
                {resolvedNote}

                <div className={styles.tableScroll}>
                  <table className={styles.feedbackTable}>
                    <caption>Sonuç türüne göre oy dağılımı</caption>
                    <thead>
                      <tr>
                        <th scope='col'>Sonuç</th>
                        <th scope='col'>Olumlu</th>
                        <th scope='col'>Olumsuz</th>
                        <th scope='col'>Olumsuzluk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedback.OutcomeFeedback.map(item => (
                        <tr
                          key={item.Key}
                          data-testid={`feedback-outcome-${item.Key}`}
                          className={item.Key === 'success' && item.Negative > 0 ? styles.feedbackAlert : undefined}
                        >
                          <th scope='row'>{item.Key}</th>
                          <td>{formatCount(item.Positive)}</td>
                          <td>{formatCount(item.Negative)}</td>
                          <td>{formatPercent(item.NegativeRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <ChatbotAnalyticsCharts data={data} />

          <div className={styles.tableGrid}>
            <section className={styles.panel} aria-labelledby='chatbot-intents-title'>
              <div className={styles.panelHeader}>
                <div>
                  <span className={styles.eyebrow}>Niyetler</span>
                  <h2 id='chatbot-intents-title'>En Çok Kullanılan Intentler</h2>
                </div>
              </div>
              <div className={styles.tableScroll}>
                <table>
                  <thead>
                    <tr>
                      <th scope='col'>Intent</th>
                      <th scope='col'>Tur</th>
                      <th scope='col'>Oran</th>
                      <th scope='col'>Ort. süre</th>
                      <th scope='col'>Oy (+/−)</th>
                      <th scope='col'>Olumsuzluk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.TopIntents.map(intent => {
                      // Geri bildirim kırılımı ayrı listeden gelir; eşleşme yoksa "—".
                      const intentFeedback = feedback.IntentFeedback.find(item => item.Key === intent.Intent)
                      return (
                        <tr key={intent.Intent}>
                          <th scope='row'>{intent.Intent}</th>
                          <td>{formatCount(intent.Count)}</td>
                          <td>{formatPercent(intent.Rate)}</td>
                          <td>{formatDuration(intent.AverageDurationMs)}</td>
                          <td>
                            {intentFeedback
                              ? `${formatCount(intentFeedback.Positive)} / ${formatCount(intentFeedback.Negative)}`
                              : '—'}
                          </td>
                          <td>{intentFeedback ? formatPercent(intentFeedback.NegativeRate) : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={styles.panel} aria-labelledby='chatbot-tools-title'>
              <div className={styles.panelHeader}>
                <div>
                  <span className={styles.eyebrow}>Araçlar</span>
                  <h2 id='chatbot-tools-title'>En Çok Kullanılan Araçlar</h2>
                </div>
              </div>
              <div className={styles.tableScroll}>
                <table>
                  <thead>
                    <tr>
                      <th scope='col'>Araç</th>
                      <th scope='col'>Kullanım</th>
                      <th scope='col'>Hata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.TopTools.map(tool => (
                      <tr key={tool.Tool}>
                        <th scope='row'>{tool.Tool}</th>
                        <td>{formatCount(tool.Count)}</td>
                        <td>{formatCount(tool.ErrorCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  )
}
